/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DeviceType, useDevice } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICON_CHEVRON_LEFT, ICON_MORE_HORIZ } from '../../Icons';
import { BUTTON_FORMAT_TRANSPARENT, BUTTON_THEME_DEFAULT, Button } from '../Button';
import { BreadcrumbsProps } from './Breadcrumbs.props';

/**
 * SDS Breadcrumbs component.
 */
export const Breadcrumbs = (props: BreadcrumbsProps): JSX.Element => {
  const { classes, currentPageName, links, maxItems, ...rest } = props;

  const breadcrumbClass = clsx(`sds-breadcrumbs__wrapper`, classes);

  // The first, second-to-last and last crumbs must remain visible.
  const numberOfCrumbs = links.length + (currentPageName ? 1 : 0);
  const numberOfInnerCrumbsToHide = Math.min(numberOfCrumbs - 3, numberOfCrumbs - (maxItems ?? 0));

  const defaultCollapsedState = maxItems != null && numberOfCrumbs > maxItems;

  // States
  const [isCollapsed, setCollapsed] = useState(defaultCollapsedState);

  // When toggled, the breadcrumb component will expand to display all links.
  const toggleOverflowButton = useCallback(() => setCollapsed((current) => !current), [setCollapsed]);

  // Handle navigation
  const navigate = useNavigate();
  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  // Device detection
  const deviceType = useDevice();
  const isMobile = deviceType === DeviceType.mobile;

  if (links.length === 0) {
    return <></>;
  }

  // Show a standalone link to the previous location for mobile view.
  if (isMobile) {
    const previousPage = links[links.length - 1];
    const { name, url } = previousPage;

    return (
      <nav aria-label="Breadcrumbs" className={breadcrumbClass} role="navigation" {...rest}>
        <Button
          classes="sds-breadcrumbs__button"
          format={BUTTON_FORMAT_TRANSPARENT}
          icon={ICON_CHEVRON_LEFT}
          onClick={(): void => handleNavigation(url)}
          text={name}
          theme={BUTTON_THEME_DEFAULT}
        />
      </nav>
    );
  }

  const breadcrumbItems = links.map((crumb, index) => {
    // If collapsed, ensure the first, second last and last crumbs remain visible.
    if (isCollapsed && links.length > 3) {
      // Add the overflow button after the first crumb.
      if (index === 1) {
        return (
          <li key="overflow">
            <Button
              aria-expanded={!isCollapsed}
              classes="sds-breadcrumbs__overflow"
              format={BUTTON_FORMAT_TRANSPARENT}
              icon={ICON_MORE_HORIZ}
              label="Click to show more breadcrumbs"
              onClick={toggleOverflowButton}
              theme={BUTTON_THEME_DEFAULT}
            />
          </li>
        );
      }

      // Range of indexes to hide
      const indexesToHide = Array.from({ length: numberOfInnerCrumbsToHide }, (_, index) => index + 1);
      const shouldBeHidden = indexesToHide.includes(index);
      if (shouldBeHidden) {
        return <React.Fragment key={`hidden-${index}`}></React.Fragment>;
      }
    }

    const { name, url } = crumb;
    const breadcrumbButton = (
      <Button
        classes="sds-breadcrumbs__button"
        format={BUTTON_FORMAT_TRANSPARENT}
        onClick={(): void => handleNavigation(url)}
        text={name}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    return <li key={crumb.name}>{breadcrumbButton}</li>;
  });

  // Add current page to the list.
  if (currentPageName) {
    breadcrumbItems.push(
      <li aria-current="page" className="sds-breadcrumbs__current" key={currentPageName}>
        <span>{currentPageName}</span>
      </li>,
    );
  }

  return (
    <nav aria-label="Breadcrumbs" className={breadcrumbClass} role="navigation" {...rest}>
      <ol className={`sds-breadcrumbs__list`}>{breadcrumbItems}</ol>
    </nav>
  );
};
